"use client";

import React, { useState, useEffect, createContext, useContext } from "react";

import { useSession } from "@/lib/auth-client";

interface AdminStatusContextType {
  isAdmin: boolean;
  isLoading: boolean;
  user: any;
  showRecommendations: boolean;
  toggleRecommendations: () => void;
}

const AdminStatusContext = createContext<AdminStatusContextType | undefined>(
  undefined,
);

export function AdminStatusProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, isPending } = useSession();
  const [showRecommendations, setShowRecommendations] = useState(false);

  const isAdmin = session?.user?.email?.endsWith("@crowdsec.net") || false;

  // Load recommendation display preference from localStorage
  useEffect(() => {
    if (isAdmin) {
      const saved = localStorage.getItem("admin-show-recommendations");

      setShowRecommendations(saved === "true");
    } else {
      setShowRecommendations(false);
    }
  }, [isAdmin]);

  const toggleRecommendations = () => {
    if (!isAdmin) return;

    const newValue = !showRecommendations;

    setShowRecommendations(newValue);
    localStorage.setItem("admin-show-recommendations", newValue.toString());
  };

  const value = {
    isAdmin,
    isLoading: isPending,
    user: session?.user || null,
    showRecommendations,
    toggleRecommendations,
  };

  return (
    <AdminStatusContext.Provider value={value}>
      {children}
    </AdminStatusContext.Provider>
  );
}

export function useAdminStatus() {
  const context = useContext(AdminStatusContext);

  if (context === undefined) {
    throw new Error(
      "useAdminStatus must be used within an AdminStatusProvider",
    );
  }

  return context;
}
